import { createShouldForwardProp } from '@styled-system/should-forward-prop';

import type { ConfigStyle } from 'styled-system';
import { flexbox, layout, position, background, typography, border, grid, boxShadow, zIndex } from 'styled-system';
import { spaceSystem, getStyledColor, borderSidesRadiusSystem } from '../../common/styledSystem/styledFunctions';

import { INVALID_PROPS_AND_STYLED_PROPS } from '../../utils/removeNonHTMLProps';
import type { ExtraSystemConfig } from '../../common/styledSystem/types';
import { filterUnique } from '../../utils/array';
import { marketingFontVariant } from '@shared/utils/fonts/marketingFonts';
import type { Theme as ThemeV1 } from '@withjoy/joykit/theme';
import type { BoxProps } from './Box.types';
import { pseudoSelectors } from '../../common/pseudos';
import { notNullOrUndefined } from '@shared/utils/notNullOrUndefined';
import deepmerge from 'deepmerge';
import { system, compose, variant } from '../../common/styledSystem/core';

const extraStyleDefinitions: Readonly<Record<keyof ExtraSystemConfig, ConfigStyle | boolean>> = {
  appearance: true,
  aspectRatio: true,
  backdropFilter: true,
  backfaceVisibility: true,
  boxSizing: true,
  clipPath: true,
  content: true,
  cursor: true,
  fill: true,
  filter: true,
  gridColumnStart: true,
  gridColumnEnd: true,
  listStyle: true,
  mixBlendMode: true,
  objectFit: true,
  objectPosition: true,
  outline: true,
  // The focus ring on a *filled* control -- a checked Toggle, a checked checkbox -- has to sit
  // off the control or it merges into the fill. `outline` alone could not express that: without
  // these two the offset was dropped silently and the colour fell back to `currentColor`.
  // See doc/field-focus-consistency/PLAN.md.
  outlineColor: { property: 'outlineColor', scale: 'colors' },
  outlineOffset: true,
  pointerEvents: true,
  stroke: true,
  textDecoration: true,
  textDecorationColor: true,
  textDecorationThickness: true,
  textDecorationStyle: true,
  textDecorationLine: true,
  textOverflow: true,
  textTransform: true,
  touchAction: true,
  transition: true,
  transform: true,
  transformOrigin: true,
  transitionDuration: true,
  transitionDelay: true,
  transitionProperty: true,
  transitionTimingFunction: true,
  userSelect: true,
  visibility: true,
  whiteSpace: true,
  willChange: true,
  wordBreak: true,
};

const extraStyleConfig = system(extraStyleDefinitions);

const styleConfigsWithScales = system({
  rowGap: {
    property: 'rowGap',
    scale: 'space',
  },
  gap: {
    property: 'gap',
    scale: 'space',
  },
  columnGap: {
    property: 'columnGap',
    scale: 'space',
  },
  inset: {
    property: 'inset',
    scale: 'space',
  },
});

const joykitV1TypeVariant = variant({
  prop: 'typeVariant',
  scale: 'typography.variants',
});

const joykitV2TypeVariant = variant({
  prop: 'typographyVariant',
  scale: 'typography.variants',
});

// Cannot add `styleFn`s (created via `system` function) to compose fn -- it will use style-system fallback breakpoints + other theme scales
// https://github.com/styled-system/styled-system/issues/2078
const systemProps = compose(
  spaceSystem,
  grid,
  getStyledColor,
  flexbox,
  layout,
  position,
  background,
  typography,
  joykitV2TypeVariant,
  joykitV1TypeVariant,
  marketingFontVariant,
  border,
  borderSidesRadiusSystem,
  boxShadow,
  zIndex,
  extraStyleConfig,
  styleConfigsWithScales,
);

const propsToNotForward = filterUnique([...INVALID_PROPS_AND_STYLED_PROPS, 'variant', 'typeVariant', 'typographyVariant', ...(systemProps.propNames || [])]).sort();

export const shouldForwardProp = createShouldForwardProp(propsToNotForward);

const SYSTEM_PROP_SET = new Set([...(systemProps.propNames || [])] as string[]);

const isStyleProp = (prop: string) => SYSTEM_PROP_SET.has(prop) || prop in pseudoSelectors;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dict<T = any> = Record<string, T>;

const _objectFilter = <T extends Dict>(object: T, fn: (value: unknown, key: keyof Dict, object: T) => boolean) => {
  const result: Dict = {};

  Object.keys(object).forEach(key => {
    const value = object[key];
    const shouldPass = fn(value, key, object);
    if (shouldPass) {
      result[key] = value;
    }
  });

  return result;
};

const filterProps = (props = {}) => {
  return _objectFilter(props, (value, key) => notNullOrUndefined(value) && isStyleProp(key));
};

/**
 * Every render of every `Box` -- and of everything built on it -- re-runs the same merge + parse.
 * On one dashboard load the parser was called 20k times for ~1k distinct results, so the results are
 * memoized per theme. The cache is bounded because the keys come from caller-supplied style props.
 */
export const STYLE_SYSTEM_PARSER_CACHE_LIMIT = 500;

const parserCacheByTheme = new WeakMap<object, Map<string, Dict>>();

// `JSON.stringify` drops `undefined` properties, which would make `{ color: undefined }` and `{}`
// share a key even though they parse differently.
const UNDEFINED_PLACEHOLDER = '\u0000undefined';

/**
 * Thrown rather than recorded on a module-level flag: `JSON.stringify` runs getters, so a getter
 * that renders another `Box` would re-enter this and reset a shared flag, letting the outer call
 * treat unkeyable input as keyable. Throwing keeps the signal on the stack it belongs to.
 */
const UNSERIALIZABLE = Symbol('unserializable style prop');

const cacheKeyReplacer = (_key: string, value: unknown) => {
  if (value === undefined) return UNDEFINED_PLACEHOLDER;
  if (typeof value === 'function' || typeof value === 'symbol') {
    throw UNSERIALIZABLE;
  }
  return value;
};

/**
 * Returns `null` when the inputs cannot be represented faithfully as a string, in which case the
 * caller must parse instead of risking two different inputs sharing a key.
 */
const buildCacheKey = (__css: Dict, styleProps: Dict): string | null => {
  try {
    return JSON.stringify([__css, styleProps], cacheKeyReplacer);
  } catch {
    // Unserializable value. (Circular input also lands here, but never gets this far in practice:
    // `deepmerge` below blows the stack on it first, with or without this cache.)
    return null;
  }
};

function styleSystemPropsParser<Theme extends ThemeV1>(props: BoxProps & { theme: Theme }) {
  const { theme, __css = {}, ...restProps } = props;
  const styleProps = filterProps(restProps);

  const cacheKey = theme ? buildCacheKey(__css, styleProps) : null;
  let cache: Map<string, Dict> | undefined;

  if (cacheKey !== null) {
    cache = parserCacheByTheme.get(theme);
    if (!cache) {
      cache = new Map();
      parserCacheByTheme.set(theme, cache);
    }
    const cached = cache.get(cacheKey);
    if (cached !== undefined) return cached;
  }

  const styles = deepmerge(__css, styleProps, {
    arrayMerge: (target, source) => source,
  });

  const parsed = systemProps({ theme, ...styles }) as Dict;

  if (cache && cacheKey !== null) {
    if (cache.size >= STYLE_SYSTEM_PARSER_CACHE_LIMIT) {
      // A `Map` iterates in insertion order, so the first key is the oldest one.
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) cache.delete(oldest);
    }
    cache.set(cacheKey, parsed);
  }

  return parsed;
}

export { styleSystemPropsParser };
